import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Grid, Card, Spin } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { JsonLd } from '../seo';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';
const BRAND = 'var(--brand-primary, #222957)';
const SITE_ORIGIN = 'https://shiningstars.tw';

// 知識頁索引（水晶入門指南）：列出所有文章卡片。內容由後台 /api/content/knowledge 供應。
export default function CrystalGuide() {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/content/knowledge`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d) return;
        if (Array.isArray(d.articles)) setArticles(d.articles);
        if (Array.isArray(d.groups)) setGroups(d.groups);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // 依分組把文章分區：先照 groups 的 order，未分組或找不到分組的歸「其他」
  const sections = (() => {
    const named = groups.filter((g) => g && g.name).map((g) => ({
      name: g.name,
      items: articles.filter((a) => a.group === g.name),
    }));
    const knownNames = new Set(groups.map((g) => g.name));
    const rest = articles.filter((a) => !a.group || !knownNames.has(a.group));
    if (rest.length) named.push({ name: '其他', items: rest });
    return named.filter((s) => s.items.length);
  })();

  // ItemList 結構化資料：幫助 Google/AI 理解這是一組知識文章清單
  const itemListLd = articles.length ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '水晶入門指南',
    itemListElement: articles.map((a, i) => ({
      '@type': 'ListItem', position: i + 1, name: a.title,
      url: `${SITE_ORIGIN}/crystal-guide/${a.slug}`,
    })),
  } : null;

  return (
    <div style={{ width: '100%', minHeight: '100%', background: isMobile ? '#fff' : '#F9FAFD' }}>
      {itemListLd && <JsonLd id="guide-list" data={itemListLd} />}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: isMobile ? '24px 18px 40px' : '48px 32px 72px' }}>
        <Title level={1} style={{ fontSize: isMobile ? 24 : 30, color: BRAND, fontWeight: 800, letterSpacing: 1, marginBottom: 8 }}>
          水晶入門指南
        </Title>
        <Paragraph type="secondary" style={{ fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 20 : 28 }}>
          第一次買水晶手鍊怎麼選？五行怎麼搭？生命靈數怎麼用？手鍊怎麼保養消磁？每篇都先給你直接答案，再補說明。
        </Paragraph>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}><Spin /></div>
        ) : (
          sections.map((sec) => (
            <div key={sec.name} style={{ marginBottom: isMobile ? 24 : 32 }}>
              <Title level={2} style={{ fontSize: isMobile ? 16 : 18, color: BRAND, fontWeight: 700, margin: '0 0 12px', paddingLeft: 2 }}>
                {sec.name}
              </Title>
              <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
                {sec.items.map((a) => (
                  <Card
                    key={a.slug}
                    hoverable
                    onClick={() => navigate(`/crystal-guide/${a.slug}`)}
                    style={{ borderRadius: 14, border: '1px solid #E5E7F5', cursor: 'pointer' }}
                    styles={{ body: { padding: isMobile ? 16 : 20 } }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <Title level={3} style={{ fontSize: isMobile ? 17 : 19, margin: '0 0 6px', color: '#242C5E' }}>
                          {a.title}
                        </Title>
                        <Text style={{ fontSize: isMobile ? 13 : 14, color: '#3E4BA3', lineHeight: 1.7 }}>
                          {a.summary}
                        </Text>
                      </div>
                      <RightOutlined style={{ color: BRAND, flexShrink: 0 }} />
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
